import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';

@Component({
  selector: 'app-comparison-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="flex items-center gap-2">
      <span>{{ formattedValue }}</span>
      @if (hasChanged) {
        <mat-icon
          class="text-orange-500"
          [matTooltip]="'Valor modificado: ' + formattedBeforeValue + ' → ' + formattedAfterValue"
          style="font-size: 16px; width: 16px; height: 16px;">
          change_circle
        </mat-icon>
      }
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
    }
  `]
})
export class ComparisonCellRendererComponent<T> implements CellRenderer<T, any> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<T, any>;

  get beforeValue(): any {
    // Acceder a cellRendererParams pasados en la colDef
    const beforeField = this.params?.colDef?.cellRendererParams?.beforeField as string;
    return this.params?.data?.[beforeField];
  }

  get afterValue(): any {
    const afterField = this.params?.colDef?.cellRendererParams?.afterField as string;
    return this.params?.data?.[afterField];
  }

  get formatter(): ((value: any) => string) | undefined {
    return this.params?.colDef?.cellRendererParams?.formatter;
  }

  get formattedBeforeValue(): string {
    if (this.formatter) {
      return this.formatter(this.beforeValue);
    }
    return this.beforeValue?.toString() ?? '-';
  }

  get formattedAfterValue(): string {
    if (this.formatter) {
      return this.formatter(this.afterValue);
    }
    return this.afterValue?.toString() ?? '-';
  }

  get formattedValue(): string {
    return this.formattedAfterValue;
  }

  get hasChanged(): boolean {
    // Comparar valores, considerando null/undefined como iguales
    if (this.beforeValue === this.afterValue) return false;
    if (this.beforeValue == null && this.afterValue == null) return false;
    if (this.beforeValue == null || this.afterValue == null) return true;

    // Para números, usar comparación con tolerancia
    if (typeof this.beforeValue === 'number' && typeof this.afterValue === 'number') {
      return Math.abs(this.beforeValue - this.afterValue) > 0.001;
    }

    return this.beforeValue !== this.afterValue;
  }
}
